import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const SHOPIFY_URL = 'https://shop.finestfeathers.com/collections/all';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Check if current page is active
  const isActive = (path) => {
    if (path === '/zen-tshirts') return location.pathname === '/zen-tshirts';
    if (path === '/native-ribbon') return location.pathname === '/native-ribbon';
    return false;
  };

  const navLinks = [
    { to: '/native-ribbon', label: 'Native Ribbon', isPage: true },
    { to: '/zen-tshirts', label: "Zen's T-Shirts", isPage: true },
    { to: '/#about', label: 'About Us', isPage: false },
    { to: '/#contact', label: 'Contact Us', isPage: false },
    { to: '/#music', label: 'Music', isPage: false },
    { to: '/#entreprenatives', label: 'EntrepreNatives', isPage: false },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-sm py-3' : 'bg-black/70 backdrop-blur-sm py-4'
      }`}
      data-testid="site-header"
    >
      <div className="container mx-auto px-4 sm:px-6 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="header-logo-link">
          <img
            src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/n560cpi8_Untitled%20design%20%2820%29.png"
            alt="Finest Feathers Logo"
            className="h-10 sm:h-12 w-auto"
          />
          <div 
            className="text-sm sm:text-xl font-bold text-white heading-oswald" 
            style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.08em' }}
          >
            FINEST FEATHERS
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex gap-8 items-center">
          {navLinks.map((link) => (
            link.isPage ? (
              <Link
                key={link.to}
                to={link.to}
                className={`transition-colors ${
                  isActive(link.to) 
                    ? 'text-orange-500 font-semibold' 
                    : 'hover:text-orange-500'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.to}
                href={link.to.replace('/', '')}
                className="hover:text-orange-500 transition-colors"
              >
                {link.label}
              </a>
            )
          ))}
        </nav>
        
        {/* Desktop Shop Now Button */}
        <Button
          onClick={() => window.open(SHOPIFY_URL, '_blank')}
          className="bg-orange-500 hover:bg-orange-600 text-black font-bold btn-orange-glow hidden md:flex"
          data-testid="header-shop-btn"
        >
          Shop Now
        </Button>
        
        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-orange-500 p-2 bg-orange-500/10 rounded-lg border border-orange-500/30"
          data-testid="mobile-menu-toggle"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-zinc-800 mt-2">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              link.isPage ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`py-2 transition-colors ${
                    isActive(link.to)
                      ? 'text-orange-500 font-semibold'
                      : 'text-white hover:text-orange-500'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.to}
                  href={link.to.replace('/', '')}
                  className="text-white hover:text-orange-500 transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <Button
              onClick={() => {
                window.open(SHOPIFY_URL, '_blank');
                setMobileMenuOpen(false);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold btn-orange-glow w-full mt-2"
            >
              Shop Now
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
